import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { gql, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { Calendar, Eye, FileCheck, FileText, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import RescheduleResponseModal from './RescheduleResponseModal';
import TS_InspectionReportModal from './TS_InspectionReportModal';
import TS_InspectionReportsViewModal from './TS_InspectionReportsViewModal';
import TS_ScheduleInspectionModal from './TS_ScheduleInspectionModal';
const DELETE_INSPECTION = gql`
   mutation DeleteInspection($id: ID!, $reason: String) {
      deleteInspection(id: $id, reason: $reason)
   }
`;

const UNDO_INSPECTION_REPORT = gql`
   mutation UndoInspectionReport($id: ID!) {
      undoInspectionReport(id: $id) {
         id
         inspectionStatus
         findings {
            result
            observations
            recommendations
         }
         permitId
         applicationNumber
         scheduledDate
         scheduledTime
         location
      }
   }
`;

const InspectionApplicationRow = ({
   application,
   inspection,
   formatInspectionStatus,
   isScheduleDisabled,
   isMobile,
   onRefetch
}) => {
   // console.log('Application object:', application);
   // console.log('Application ID:', application?.id);
   // console.log('Inspection object:', inspection);

   const [showScheduleModal, setShowScheduleModal] = useState(false);
   const [showReportModal, setShowReportModal] = useState(false);
   const [showUndoDialog, setShowUndoDialog] = useState(false);
   const [cancellationReason, setCancellationReason] = useState('');
   const [showInspectionReports, setShowInspectionReports] = useState(false);
   const [showRescheduleModal, setShowRescheduleModal] = useState(false);
   const [showViewModal, setShowViewModal] = useState(false);

   const [deleteInspection] = useMutation(DELETE_INSPECTION, {
      onCompleted: () => {
         toast.success('Inspection schedule cancelled successfully');
         onRefetch();
      },
      onError: (error) => {
         toast.error(`Error cancelling inspection: ${error.message}`);
      }
   });

   const [undoInspectionReport] = useMutation(UNDO_INSPECTION_REPORT, {
      onCompleted: () => {
         toast.success('Inspection report has been undone');
         onRefetch?.();
      },
      onError: (error) => {
         toast.error(`Error undoing inspection report: ${error.message}`);
      }
   });

   const handleSchedule = () => {
      setShowScheduleModal(true);
   };

   const handleComplete = () => {
      setShowReportModal(true);
   };

   const handleScheduleComplete = () => {
      setShowScheduleModal(false);
      onRefetch();
   };

   const handleReportComplete = () => {
      setShowReportModal(false);
      onRefetch();
   };

   const handleUndo = () => {
      setShowUndoDialog(true);
   };

   const handleUndoConfirm = async () => {
      try {
         await deleteInspection({
            variables: {
               id: inspection.id,
               ...(cancellationReason.trim() && { reason: cancellationReason.trim() })
            }
         });
         setShowUndoDialog(false);
         setCancellationReason('');
      } catch (error) {
         console.error('Error deleting inspection:', error);
      }
   };

   const handleUndoReport = async () => {
      try {
         await undoInspectionReport({
            variables: {
               id: inspection.id
            }
         });
      } catch (error) {
         console.error('Error undoing inspection report:', error);
         toast.error(`Failed to undo inspection report: ${error.message}`);
      }
   };

   const renderActionButtons = () => {
      const actions = [];

      // Show Undo button if inspection exists and status is Pending, Accepted, or Reschedule Requested
      if (inspection && (
         inspection.inspectionStatus === 'Pending' ||
         inspection.inspectionStatus === 'Accepted' ||
         inspection.inspectionStatus === 'Reschedule Requested'
      )) {
         actions.push(
            <TooltipProvider key="undo-action" delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={handleUndo}
                        variant="outline"
                        size="icon"
                        className="text-yellow-600 hover:text-yellow-800"
                     >
                        <Undo2 className="h-4 w-4" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                     <p>
                        {inspection.inspectionStatus === 'Reschedule Requested'
                           ? 'Undo Reschedule Request'
                           : 'Cancel Schedule'}
                     </p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );
      }

      // Only show Schedule button if there's no inspection
      if (!inspection) {
         actions.push(
            <TooltipProvider key="schedule-action" delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={handleSchedule}
                        variant="outline"
                        size="icon"
                     >
                        <Calendar className="h-4 w-4" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                     <p>Schedule Inspection</p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );
      }

      // Complete inspection action
      if (inspection?.inspectionStatus === 'Accepted') { // only if accepted by applicant
         actions.push(
            <TooltipProvider key="complete-action" delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={handleComplete}
                        variant="outline"
                        size="icon"
                        className="text-blue-600 hover:text-blue-800"
                     >
                        <FileCheck className="h-4 w-4" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                     <p>Complete Inspection</p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );
      }

      // undo report button for completed inspections
      if (inspection?.inspectionStatus === 'Completed') {
         actions.push(
            <TooltipProvider key="undo-report-action" delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={handleUndoReport}
                        variant="outline"
                        size="icon"
                        className="text-yellow-600 hover:text-yellow-800"
                     >
                        <Undo2 className="h-4 w-4" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                     <p>Undo Inspection Report</p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );

         // view reports button only for completed inspections
         actions.push(
            <TooltipProvider key="view-reports-action" delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={() => setShowInspectionReports(true)}
                        variant="outline"
                        size="icon"
                        className="text-blue-600 hover:text-blue-800"
                     >
                        <FileText className="h-4 w-4" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                     <p>View Inspection Reports</p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );
      }

      // Show Respond button for reschedule requests
      if (inspection?.inspectionStatus === 'Reschedule Requested') {
         actions.push(
            <TooltipProvider key="respond-action" delayDuration={200}>
               <Tooltip>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={() => setShowRescheduleModal(true)}
                        variant="outline"
                        size="icon"
                        className="text-blue-600 hover:text-blue-800"
                     >
                        <Calendar className="h-4 w-4" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                     <p>Respond to Reschedule</p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         );
      }

      //  view application button
      actions.push(
         <TooltipProvider key="view-application-action" delayDuration={200}>
            <Tooltip>
               <TooltipTrigger asChild>
                  <Button
                     onClick={() => setShowViewModal(true)}
                     variant="outline"
                     size="icon"
                     className="text-blue-600 hover:text-blue-800"
                  >
                     <Eye className="h-4 w-4" />
                  </Button>
               </TooltipTrigger>
               <TooltipContent>
                  <p>View Application</p>
               </TooltipContent>
            </Tooltip>
         </TooltipProvider>
      );

      return actions;
   };

   if (isMobile) {
      return (
         <>
            <Card className="p-4 space-y-3">
               <div className="flex justify-between items-start">
                  <div>
                     <p className="font-medium">{application.applicationNumber}</p>
                     <p className="text-sm text-muted-foreground">{application.applicationType}</p>
                  </div>
                  <Badge
                     variant={getStatusVariant(inspection?.inspectionStatus).variant}
                     className={getStatusVariant(inspection?.inspectionStatus).className}
                  >
                     {formatInspectionStatus(inspection)}
                  </Badge>
               </div>
               <p className="text-sm text-muted-foreground">
                  {format(new Date(application.dateOfSubmission), 'MMM d, yyyy')}
               </p>
               <div className="flex gap-2">
                  {renderActionButtons()}
               </div>
            </Card>

            {/* Modals */}
            {showScheduleModal && (
               <TS_ScheduleInspectionModal
                  isOpen={showScheduleModal}
                  onClose={() => setShowScheduleModal(false)}
                  application={application}
                  onScheduleComplete={handleScheduleComplete}
               />
            )}

            {showReportModal && (
               <TS_InspectionReportModal
                  isOpen={showReportModal}
                  onClose={() => setShowReportModal(false)}
                  inspection={inspection}
                  application={application}
                  onComplete={handleReportComplete}
               />
            )}

            <AlertDialog open={showUndoDialog} onOpenChange={(open) => {
               setShowUndoDialog(open);
               if (!open) setCancellationReason('');
            }}>
               <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>Cancel Inspection Schedule</AlertDialogTitle>
                     <AlertDialogDescription>
                        Are you sure you want to cancel this inspection schedule? This action cannot be undone.
                     </AlertDialogDescription>
                  </AlertDialogHeader>

                  <div className="space-y-2 py-4">
                     <label htmlFor="reason" className="text-sm font-medium">
                        Reason for Cancellation
                     </label>
                     <Input
                        id="reason"
                        placeholder="Enter reason for cancellation"
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                     />
                  </div>

                  <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                     <AlertDialogAction onClick={handleUndoConfirm}>
                        Confirm
                     </AlertDialogAction>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialog>

            {showInspectionReports && (
               <TS_InspectionReportsViewModal
                  isOpen={showInspectionReports}
                  onClose={() => setShowInspectionReports(false)}
                  permitId={application?.id}
               />
            )}

            {showRescheduleModal && (
               <RescheduleResponseModal
                  isOpen={showRescheduleModal}
                  onClose={() => setShowRescheduleModal(false)}
                  inspection={inspection}
                  onRefetch={onRefetch}
               />
            )}

            <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
               <DialogContent>
                  <DialogHeader>
                     <DialogTitle>Application Details</DialogTitle>
                     <DialogDescription>
                        Viewing details for Application {application?.applicationNumber}
                     </DialogDescription>
                  </DialogHeader>

                  <div className="mt-4">
                     <ApplicationViewModal
                        isOpen={showViewModal}
                        onClose={() => setShowViewModal(false)}
                        application={application}
                     />
                  </div>
               </DialogContent>
            </Dialog>
         </>
      );
   }

   return (
      <>
         <TableRow>
            <TableCell className="w-[20%] text-center">{application.applicationNumber}</TableCell>
            <TableCell className="w-[20%] text-center">{application.applicationType}</TableCell>
            <TableCell className="w-[15%] text-center">{format(new Date(application.dateOfSubmission), 'MMM d, yyyy')}</TableCell>
            <TableCell className="w-[20%] text-center">

               <Badge
                  variant={getStatusVariant(inspection?.inspectionStatus).variant}
                  className={getStatusVariant(inspection?.inspectionStatus).className}
               >
                  {formatInspectionStatus(inspection)}
               </Badge>
            </TableCell>
            <TableCell className="w-[10%] text-center">
               <div className="flex justify-center gap-2">
                  {renderActionButtons()}
               </div>
            </TableCell>
         </TableRow>

         {/* Modals */}
         {showScheduleModal && (
            <TS_ScheduleInspectionModal
               isOpen={showScheduleModal}
               onClose={() => setShowScheduleModal(false)}
               application={application}
               onScheduleComplete={handleScheduleComplete}
            />
         )}

         {showReportModal && (
            <TS_InspectionReportModal
               isOpen={showReportModal}
               onClose={() => setShowReportModal(false)}
               inspection={inspection}
               application={application}
               onComplete={handleReportComplete}
            />
         )}

         <AlertDialog open={showUndoDialog} onOpenChange={(open) => {
            setShowUndoDialog(open);
            if (!open) setCancellationReason('');
         }}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Inspection Schedule</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to cancel this inspection schedule? This action cannot be undone.
                  </AlertDialogDescription>
               </AlertDialogHeader>

               <div className="space-y-2 py-4">
                  <label htmlFor="reason" className="text-sm font-medium">
                     Reason for Cancellation
                  </label>
                  <Input
                     id="reason"
                     placeholder="Enter reason for cancellation"
                     value={cancellationReason}
                     onChange={(e) => setCancellationReason(e.target.value)}
                  />
               </div>

               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleUndoConfirm}>
                     Confirm
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         {showInspectionReports && (
            <TS_InspectionReportsViewModal
               isOpen={showInspectionReports}
               onClose={() => setShowInspectionReports(false)}
               permitId={application?.id}
            />
         )}

         {showRescheduleModal && (
            <RescheduleResponseModal
               isOpen={showRescheduleModal}
               onClose={() => setShowRescheduleModal(false)}
               inspection={inspection}
               onRefetch={onRefetch}
            />
         )}

         <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
            <DialogContent>
               <DialogHeader>
                  <DialogTitle>Application Details</DialogTitle>
                  <DialogDescription>
                     Viewing details for Application {application?.applicationNumber}
                  </DialogDescription>
               </DialogHeader>

               <div className="mt-4">
                  <ApplicationViewModal
                     isOpen={showViewModal}
                     onClose={() => setShowViewModal(false)}
                     application={application}
                  />
               </div>
            </DialogContent>
         </Dialog>
      </>
   );
};

const getStatusVariant = (status) => {
   switch (status?.toLowerCase()) {
      case 'pending':
         return {
            variant: 'warning',
            className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80'
         };
      case 'completed':
         return {
            variant: 'success',
            className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
         };
      case 'cancelled':
         return {
            variant: 'destructive',
            className: 'bg-red-100 text-red-800 hover:bg-red-100/80'
         };
      default:
         return {
            variant: 'secondary',
            className: 'bg-gray-100 text-gray-800 hover:bg-gray-100/80'
         };
   }
};

export default InspectionApplicationRow;
